import React, { useState, useCallback, useEffect } from 'react';
import { Box, MenuItem, SxProps, Typography, useTheme } from '@mui/material';
import {
  MentionsInput,
  Mention,
  SuggestionDataItem,
  MentionsInputStyle
} from 'react-mentions';
import './templated-input.css';
import { FieldWrapper } from 'common/form';

const DEFAULT_SUB_LABEL = `▪ You must include an opt-out message with the "STOP" keyword
▪ Personalise for multiple recipients: type @ or { and choose a variable`;

const mentionsInputStyle = ({
  itemBackgroundColor,
  borderColor,
  textColor,
  minHeight
}: {
  itemBackgroundColor: string;
  listBorderColor: string;
  borderColor: string | undefined;
  textColor: string;
  minHeight?: number;
  focusBorderColor?: string;
}): MentionsInputStyle => ({
  control: {
    fontSize: 16,
    height: '100%'
  },
  input: {
    padding: 16,
    width: '100%',
    color: textColor,
    borderColor,
    border: '1px solid',
    minHeight,
    overflow: 'scroll'
  },
  highlighter: {
    padding: 16,
    height: '100%'
  },
  suggestions: {
    list: {
      backgroundColor: 'white',
      border: `1px solid`,
      fontSize: 16
    },
    item: {
      padding: '8px 12px',
      '&focused': {
        backgroundColor: itemBackgroundColor
      }
    }
  }
});

const menuItemContainer = (primaryColor: string): SxProps => ({
  ':hover': {
    backgroundColor: primaryColor
  },
  '&.Mui-selected': {
    ':hover': {
      backgroundColor: primaryColor
    },
    backgroundColor: primaryColor
  },
  px: 2,
  py: 1
});

const characterCount: SxProps = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  mt: 1,
  gap: 1,
  fontSize: 'sm',
  color: 'text.secondary'
};

const mentionStyle = (backgroundColor: string) => ({
  backgroundColor
});

export type Variable = {
  label: string;
  value: string;
};

type Props = {
  onChange: (template: string, charactersCount: number) => void;
  charactersCountLimit?: number;
  placeholder?: string;
  errorMessage?: string;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  id?: string;
  name?: string;
  minHeight?: number;
  field: {
    availableVariables: Variable[];
    label?: string | JSX.Element;
    subLabel?: string | JSX.Element;
  };
  value?: string;
};

const TemplatedInput: React.FC<Props> = ({
  field: { availableVariables, label, subLabel = DEFAULT_SUB_LABEL },
  onChange,
  charactersCountLimit,
  placeholder,
  onBlur,
  minHeight,
  errorMessage,
  id,
  value: initialValue,
  name
}) => {
  const transformFromVariableValue = useCallback(
    (value: string) =>
      availableVariables.reduce((transformedValue, variable) => {
        return transformedValue.replaceAll(
          `{{${variable.value}}}`,
          `[(${variable.label})]{{${variable.value}}}`
        );
      }, value),
    [availableVariables]
  );

  const transformFromVariableLabel = useCallback(
    (value: string) =>
      availableVariables.reduce((transformedValue, variable) => {
        return transformedValue.replaceAll(
          `{{${variable.label}}}`,
          `[(${variable.label})]{{${variable.value}}}`
        );
      }, value),
    [availableVariables]
  );

  const [value, setValue] = useState(
    transformFromVariableValue(initialValue || '')
  );

  useEffect(() => {
    setValue(transformFromVariableValue(initialValue || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, transformFromVariableValue]);

  const { palette } = useTheme();

  // replace {{ }} and [()] with empty string to get the character count
  const charactersCount = value
    .replaceAll(/{{(.*?)}}/g, '')
    .replaceAll(/\[\((.*?)\)\]/g, '').length;

  const handleChange = useCallback(
    (event: { target: { value: string } }) => {
      const newValue = event.target.value;

      const transformedValueFromLabel = transformFromVariableLabel(newValue);
      const transformedValueForOnChange = transformedValueFromLabel.replace(
        /\[\((.*?)\)\]/g,
        ''
      );

      // transform label to value
      setValue(transformFromVariableLabel(newValue));
      onChange(transformedValueForOnChange, charactersCount);
    },
    [onChange, charactersCount, transformFromVariableLabel]
  );

  const variables = availableVariables.map((variable) => ({
    id: variable.value,
    display: variable.label
  }));

  const renderSuggestion = useCallback(
    (suggestion: SuggestionDataItem) => (
      <MenuItem
        sx={menuItemContainer(palette.customGrey.light)}
        key={suggestion.id}
      >
        <Typography variant="body2">{suggestion.display}</Typography>
      </MenuItem>
    ),
    [palette.customGrey.light]
  );

  return (
    <>
      <FieldWrapper
        label={label}
        errorMessage={errorMessage}
        subLabel={subLabel}
        sx={{ minHeight }}
      >
        {/* @ts-expect-error:  Type 'FunctionComponent<MentionProps>' is not assignable to type '(props: any, deprecatedLegacyContext?: any) => ReactNode'. */}
        <MentionsInput
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          style={mentionsInputStyle({
            itemBackgroundColor: palette.customGrey.light,
            listBorderColor: palette.customGrey.dark,
            borderColor: errorMessage
              ? palette.error.main
              : palette.customGrey.medium,
            focusBorderColor: palette.primary.main,
            textColor: palette.text.primary,
            minHeight
          })}
          placeholder={placeholder}
          className="mentions"
          onBlur={onBlur}
        >
          {(['@', '#', '{'] as const).map((trigger) => (
            // @ts-expect-error: Type 'FunctionComponent<MentionProps>' is not assignable to type '(props: any, deprecatedLegacyContext?: any) => ReactNode'.
            <Mention
              key={trigger}
              trigger={trigger}
              data={variables}
              renderSuggestion={renderSuggestion}
              displayTransform={(id, display) => `{{${display}}}`}
              appendSpaceOnAdd
              markup="[(__display__)]{{__id__}}"
              style={mentionStyle(palette.successV2.light)}
            />
          ))}
        </MentionsInput>
      </FieldWrapper>
      {charactersCountLimit && (
        <Box sx={characterCount}>
          <Typography
            variant="body2"
            color={errorMessage ? 'error' : 'textSecondary'}
          >
            {charactersCount} / {charactersCountLimit} characters used
          </Typography>
        </Box>
      )}
    </>
  );
};

export { TemplatedInput };
